// Public types for the LOCAL multi-profile personalisation bridge.
// Renderer consumes this module; the runtime exports the matching implementation.
// Keeps session/controller.ts untouched: this file imports LocalStatus only as a type.

import type { LocalStatus } from '../session/controller.js';

export interface LocalIdentityTarget {
  personId: string;
  displayName: string;
}

export type LocalIdentityStatus = LocalStatus & {
  identityProfiles?: boolean;
  scopedPersonId?: string;
};

export interface LocalIdentityResult {
  matched: boolean;
  score: number;
  experimental: true;
  status: 'matched' | 'unknown' | 'ambiguous';
  personId?: string;
  displayName?: string;
}

export interface LocalIdentityBridge {
  status(args?: { personId: string }): Promise<LocalIdentityStatus>;
  installModels(args: { confirm: true }): Promise<unknown>;
  removeModels(args: { confirm: true }): Promise<unknown>;
  transcribe(args: { samples: number[]; sampleRate: 16000 }): Promise<{ text: string; elapsedMs: number }>;
  speak(args: { text: string }): Promise<unknown>;
  stop(): Promise<unknown>;
  voiceEmbedding(args: { samples: number[]; sampleRate: 16000 }): Promise<number[]>;
  enrollFace(args: { embeddings: number[][]; consent: true; personId?: string; displayName?: string }): Promise<{ ok: true; kind: string; dim: number; sampleCount: number }>;
  enrollVoice(args: { embeddings: number[][]; consent: true; personId?: string; displayName?: string }): Promise<{ ok: true; kind: string; dim: number; sampleCount: number }>;
  matchFace(args: { embedding: number[]; personId?: string }): Promise<{ matched: boolean; score: number; experimental: true }>;
  matchVoice(args: { embedding: number[]; personId?: string }): Promise<{ matched: boolean; score: number; experimental: true }>;
  deleteBiometrics(args: { kind: 'face' | 'voice' | 'all'; confirm: true; personId?: string }): Promise<unknown>;
  removeProfile?(args: { personId: string; confirm: true }): Promise<unknown>;
  removeAccountBiometrics?(args: { confirm: true }): Promise<{ ok: true }>;
  identifyFace?(args: { embedding: number[]; personIds: string[] }): Promise<LocalIdentityResult>;
  identifyVoice?(args: { embedding: number[]; personIds: string[] }): Promise<LocalIdentityResult>;
}

export function hasScopedIdentityBridge(local: unknown): local is LocalIdentityBridge {
  if (!local || typeof local !== 'object') return false;
  const candidate = local as Partial<LocalIdentityBridge>;
  return typeof candidate.status === 'function'
    && typeof candidate.removeProfile === 'function'
    && typeof candidate.identifyFace === 'function'
    && typeof candidate.identifyVoice === 'function';
}

export function validateLocalIdentityTarget(target: unknown): LocalIdentityTarget {
  if (!target || typeof target !== 'object') throw new TypeError('profile target required');
  const raw = target as Partial<LocalIdentityTarget>;
  if (typeof raw.personId !== 'string' || raw.personId.length === 0 || raw.personId.length > 64 || !/^[A-Za-z0-9_-]+$/u.test(raw.personId)) throw new RangeError('personId must be ascii identifier');
  if (typeof raw.displayName !== 'string') throw new TypeError('displayName required');
  const trimmed = raw.displayName.replace(/^\s+|\s+$/gu, '');
  if (trimmed.length === 0 || trimmed.length > 80) throw new RangeError('displayName length out of range');
  if (/[\u0000-\u001f\u007f]/u.test(trimmed)) throw new RangeError('displayName has control chars');
  return { personId: raw.personId, displayName: trimmed };
}

export function normalizeCandidateAllowlist(personIds: unknown): string[] {
  if (!Array.isArray(personIds)) throw new TypeError('personIds allowlist required');
  if (personIds.length === 0) return [];
  if (personIds.length > 5) throw new RangeError('allowlist too large');
  const seen = new Set<string>();
  const out: string[] = [];
  for (const value of personIds) {
    if (typeof value !== 'string' || value.length === 0 || value.length > 64 || !/^[A-Za-z0-9_-]+$/u.test(value)) throw new RangeError('invalid personId in allowlist');
    if (seen.has(value)) throw new RangeError('duplicate personId in allowlist');
    seen.add(value);
    out.push(value);
  }
  return out;
}
